import { microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useCallback, useEffect, useState } from 'react';

/** Admin accounts, which hold the Azure roles onboarding needs, sign in as azadm.… or adm.…. */
const ADMIN_ACCOUNT_PREFIXES = ['azadm.', 'adm.'];

/**
 * Added to an admin account's ownership refs at sign-in so it may run
 * templates; not a catalog group. Must match ADMIN_ACCOUNT_REF in
 * packages/backend/src/auth/adminAccountSignIn.ts.
 */
export const ADMIN_ACCOUNT_MARKER_REF = 'group:default/vivicta-admin-accounts';

export const isAdminAccount = (account: string) =>
  ADMIN_ACCOUNT_PREFIXES.some(prefix =>
    account.toLowerCase().startsWith(prefix),
  );

/**
 * The sign-in name in an Entra ID token (`preferred_username`, else `upn`).
 * The profile's email can't tell accounts apart: an admin account's mail
 * attribute is often an alias of the person's normal address.
 */
export function signInNameFromIdToken(idToken: string): string | undefined {
  try {
    const payload = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(globalThis.atob(payload)) as {
      preferred_username?: string;
      upn?: string;
    };
    return claims.preferred_username ?? claims.upn;
  } catch {
    return undefined;
  }
}

export type MicrosoftAccount =
  | { state: 'checking' }
  /** No Microsoft session, e.g. local dev signed in as guest: nothing to check. */
  | { state: 'unknown' }
  | { state: 'known'; account: string; admin: boolean };

/** The Microsoft account the portal is signed in with, and whether it is an admin account. */
export function useMicrosoftAccount(): MicrosoftAccount {
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const [account, setAccount] = useState<MicrosoftAccount>({
    state: 'checking',
  });

  useEffect(() => {
    let active = true;
    (async () => {
      const idToken = await microsoftAuth
        .getIdToken({ optional: true })
        .catch(() => '');
      const name =
        (idToken && signInNameFromIdToken(idToken)) ||
        (await microsoftAuth
          .getProfile({ optional: true })
          .then(profile => profile?.email)
          .catch(() => undefined));
      if (!active) return;
      setAccount(
        name
          ? { state: 'known', account: name, admin: isAdminAccount(name) }
          : { state: 'unknown' },
      );
    })();
    return () => {
      active = false;
    };
  }, [microsoftAuth]);

  return account;
}

/**
 * Switches the portal to another Microsoft account: ends the Microsoft
 * session and opens Microsoft's account list straight from the click (a popup
 * opened later, from the sign-in page, would be blocked), then reloads so the
 * portal signs in as the chosen account. Closing the list lands on the
 * sign-in page instead.
 */
export function useSwitchMicrosoftAccount(
  reload: () => void = () => globalThis.location.reload(),
) {
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const [switching, setSwitching] = useState(false);
  const switchAccount = useCallback(async () => {
    setSwitching(true);
    try {
      await microsoftAuth.signOut();
      await microsoftAuth.getBackstageIdentity({ instantPopup: true });
    } catch {
      // Closed or blocked: the sign-in page offers the same choice.
    }
    reload();
  }, [microsoftAuth, reload]);
  return { switchAccount, switching };
}
